import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { X, Trash2, Share2 } from 'lucide-react'
import { api, type Feedback, type ProjectWithStats } from '../lib/api'
import { Stars, Badge, Spinner, Empty, timeAgo, ConfirmModal } from '../components/ui'

const TAGS = ['bug', 'feature', 'praise', 'performance', 'other']
const STATUSES = ['open', 'resolved']

const TAG_COLORS: Record<string, { bg: string; color: string }> = {
  bug:         { bg: '#ffdad6', color: '#93000a' },
  feature:     { bg: '#ffdbcc', color: '#351000' },
  praise:      { bg: '#b6b4ff', color: '#1b1978' },
  performance: { bg: '#c8f0d8', color: '#003920' },
  other:       { bg: '#e0e3e5', color: '#464555' },
}

export function FeedbackList() {
  const [searchParams] = useSearchParams()
  const initialProject = searchParams.get('project') ?? ''

  const [projects, setProjects] = useState<ProjectWithStats[]>([])
  const [projectId, setProjectId] = useState(initialProject)
  const [items, setItems] = useState<Feedback[]>([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<Feedback | null>(null)
  const [note, setNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [lightbox, setLightbox] = useState<string | null>(null)
  const [filterTag, setFilterTag] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [page, setPage] = useState(1)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  useEffect(() => {
    api.projects.list().then(({ projects: p }) => {
      setProjects(p)
      if (!projectId && p[0]) setProjectId(p[0].id)
    })
  }, [])

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await api.feedback.list(projectId, {
        page: String(page), limit: '25',
        ...(filterTag ? { tag: filterTag } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
      })
      setItems(res.data)
      setPagination({ page: res.pagination.page, pages: res.pagination.pages, total: res.pagination.total })
    } finally {
      setLoading(false)
    }
  }, [projectId, page, filterTag, filterStatus])

  useEffect(() => { load() }, [load])

  async function setStatus(id: string, status: 'open' | 'resolved') {
    const updated = await api.feedback.update(id, { status })
    setItems(prev => prev.map(f => f.id === id ? updated : f))
    if (selected?.id === id) setSelected(updated)
  }

  async function saveNote() {
    if (!selected) return
    setSavingNote(true)
    try {
      const updated = await api.feedback.update(selected.id, { internal_note: note })
      setItems(prev => prev.map(f => f.id === selected.id ? updated : f))
      setSelected(updated)
    } finally { setSavingNote(false) }
  }

  async function handleTag(id: string, tag: string) {
    const updated = await api.feedback.update(id, { tag: tag as Feedback['tag'] })
    setItems(prev => prev.map(f => f.id === id ? updated : f))
    if (selected?.id === id) setSelected(updated)
  }

  function handleDelete(id: string) {
    setConfirmDelete(id)
  }

  async function confirmDeleteFeedback() {
    if (!confirmDelete) return
    const id = confirmDelete
    setConfirmDelete(null)
    await api.feedback.delete(id)
    setItems(prev => prev.filter(f => f.id !== id))
    if (selected?.id === id) setSelected(null)
  }

  function openDetail(f: Feedback) {
    setSelected(f)
    setNote(f.internal_note ?? '')
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>

      {/* Left Panel: Table (65%) */}
      <section style={{
        width: '65%', height: '100%', display: 'flex', flexDirection: 'column',
        padding: 32, overflowY: 'auto',
      }}>
        {/* Filters Row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            {/* Project selector */}
            <div style={{ position: 'relative' }}>
              <select
                value={projectId}
                onChange={e => { setProjectId(e.target.value); setPage(1) }}
                style={{
                  appearance: 'none', background: '#f2f4f6', border: 'none',
                  borderRadius: 8, padding: '8px 36px 8px 14px', fontSize: 13,
                  fontFamily: 'Inter, sans-serif', color: '#464555', outline: 'none', cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#777587', pointerEvents: 'none', fontSize: 12 }}>▾</span>
            </div>

            {/* Tag filter */}
            <div style={{ position: 'relative' }}>
              <select
                value={filterTag}
                onChange={e => { setFilterTag(e.target.value); setPage(1) }}
                style={{
                  appearance: 'none', background: '#f2f4f6', border: 'none',
                  borderRadius: 8, padding: '8px 36px 8px 14px', fontSize: 13,
                  fontFamily: 'Inter, sans-serif', color: '#464555', outline: 'none', cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                <option value="">All Tags</option>
                {TAGS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#777587', pointerEvents: 'none', fontSize: 12 }}>▾</span>
            </div>

            {/* Status filter */}
            <div style={{ position: 'relative' }}>
              <select
                value={filterStatus}
                onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
                style={{
                  appearance: 'none', background: '#f2f4f6', border: 'none',
                  borderRadius: 8, padding: '8px 36px 8px 14px', fontSize: 13,
                  fontFamily: 'Inter, sans-serif', color: '#464555', outline: 'none', cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                <option value="">All Status</option>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#777587', pointerEvents: 'none', fontSize: 12 }}>▾</span>
            </div>

            {(filterTag || filterStatus) && (
              <button
                onClick={() => { setFilterTag(''); setFilterStatus(''); setPage(1) }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#777587', fontSize: 12, padding: '6px 10px' }}
              >
                Clear ×
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#777587' }}>
            Showing <span style={{ color: '#191c1e', fontWeight: 700 }}>{items.length}</span> of {pagination.total} entries
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Empty icon="💬" title="No feedback found"
              sub={filterTag || filterStatus ? 'Try adjusting your filters' : 'Install the SDK to start receiving feedback'} />
          </div>
        ) : (
          <>
            <div style={{ background: '#ffffff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(25,28,30,0.07)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#f2f4f6' }}>
                  <tr>
                    {['Rating', 'Feedback', 'Tag', 'Status', 'Source', 'Time', ''].map(h => (
                      <th key={h} style={{
                        padding: '14px 20px', textAlign: 'left', fontSize: 10,
                        fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#777587',
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(f => {
                    const isActive = selected?.id === f.id
                    const tagColors = f.tag ? TAG_COLORS[f.tag] ?? TAG_COLORS.other : null
                    const titleText = f.message.slice(0, 48) + (f.message.length > 48 ? '…' : '')
                    const previewText = f.message.length > 48 ? f.message.slice(48, 90) + (f.message.length > 90 ? '…' : '') : ''
                    return (
                      <tr
                        key={f.id}
                        onClick={() => openDetail(f)}
                        className="feedback-row"
                        style={{
                          cursor: 'pointer',
                          background: isActive ? 'rgba(53,37,205,0.05)' : '#ffffff',
                          borderLeft: `4px solid ${isActive ? '#3525cd' : 'transparent'}`,
                          borderBottom: '1px solid #f2f4f6',
                          transition: 'background 0.1s',
                        }}
                      >
                        <td style={{ padding: '18px 20px 18px 16px' }}>
                          <Stars rating={f.rating} size={13} />
                        </td>
                        <td style={{ padding: '18px 20px', maxWidth: 260 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: '#191c1e', margin: 0, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {titleText}
                          </p>
                          {previewText && (
                            <p style={{ fontSize: 11, color: '#777587', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {previewText}
                            </p>
                          )}
                        </td>
                        <td style={{ padding: '18px 20px' }}>
                          {f.tag && tagColors ? (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center',
                              padding: '2px 8px', borderRadius: 4,
                              background: tagColors.bg, color: tagColors.color,
                              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                            }}>
                              {f.tag}
                            </span>
                          ) : (
                            <span style={{ color: '#c7c4d8', fontSize: 12 }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '18px 20px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            fontSize: 12, fontWeight: 600,
                            color: f.status === 'open' ? '#3525cd' : '#777587',
                          }}>
                            <span style={{
                              width: 6, height: 6, borderRadius: '50%',
                              background: f.status === 'open' ? '#3525cd' : '#777587',
                              flexShrink: 0,
                            }} />
                            {f.status === 'open' ? 'Open' : 'Resolved'}
                          </span>
                        </td>
                        <td style={{ padding: '18px 20px' }}>
                          <span style={{ fontSize: 12, color: '#777587', textTransform: 'capitalize' }}>
                            {f.platform ?? '—'}
                          </span>
                        </td>
                        <td style={{ padding: '18px 20px', fontSize: 11, color: '#777587', whiteSpace: 'nowrap' }}>
                          {timeAgo(f.created_at)}
                        </td>
                        <td style={{ padding: '18px 20px', textAlign: 'right' }}>
                          <button
                            onClick={e => { e.stopPropagation(); handleDelete(f.id) }}
                            className="delete-btn"
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: '#777587', padding: 6, borderRadius: 6,
                              opacity: 0, transition: 'all 0.15s',
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 24 }}>
              <p style={{ fontSize: 12, color: '#777587', margin: 0 }}>
                Page <strong style={{ color: '#191c1e' }}>{pagination.page}</strong> of {pagination.pages}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  style={{
                    padding: '7px 16px', background: '#eceef0', border: 'none',
                    borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#464555',
                    cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Previous
                </button>
                <button
                  disabled={page >= pagination.pages}
                  onClick={() => setPage(p => p + 1)}
                  style={{
                    padding: '7px 16px', background: '#3525cd', border: 'none',
                    borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#ffffff',
                    cursor: page >= pagination.pages ? 'not-allowed' : 'pointer',
                    opacity: page >= pagination.pages ? 0.5 : 1,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Right Panel: Detail View (35%) */}
      <section style={{
        width: '35%', height: '100%', background: '#f2f4f6',
        borderLeft: '1px solid rgba(199,196,216,0.2)',
        overflowY: 'auto',
      }}>
        {!selected ? (
          <div style={{
            height: '100%', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 12, padding: 40,
          }}>
            <div style={{ fontSize: 48 }}>💬</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#191c1e', fontFamily: 'Manrope, sans-serif' }}>Select a feedback item</div>
            <div style={{ fontSize: 13, color: '#777587', textAlign: 'center' }}>Click any row to view full details and manage the submission</div>
          </div>
        ) : (
          <div style={{ padding: 32 }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: 0, letterSpacing: '-0.02em' }}>
                Feedback Detail
              </h3>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  style={{ padding: 8, background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#777587', display: 'flex' }}
                  onClick={() => navigator.clipboard.writeText(selected.message)}
                  title="Copy message"
                >
                  <Share2 size={18} />
                </button>
                <button
                  onClick={() => handleDelete(selected.id)}
                  style={{ padding: 8, background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#ba1a1a', display: 'flex' }}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            {/* Message Card */}
            <div style={{
              background: '#ffffff', borderRadius: 12, padding: 24,
              boxShadow: '0 1px 4px rgba(25,28,30,0.07)', marginBottom: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <Stars rating={selected.rating} size={20} />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#777587' }}>{selected.rating}.0 / 5.0</span>
              </div>
              <h4 style={{
                fontSize: 15, fontWeight: 700, color: '#191c1e', margin: '0 0 10px',
                fontFamily: 'Manrope, sans-serif', lineHeight: 1.4,
              }}>
                "{selected.message.slice(0, 80)}{selected.message.length > 80 ? '…' : ''}"
              </h4>
              <p style={{ fontSize: 13, color: '#464555', lineHeight: 1.6, margin: 0 }}>
                {selected.message}
              </p>
            </div>

            {/* Screenshot */}
            {selected.screenshot_url && (
              <div style={{ marginBottom: 24 }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#777587', marginBottom: 10, paddingLeft: 2 }}>
                  Attachment
                </p>
                <div style={{ position: 'relative', cursor: 'zoom-in', borderRadius: 12, overflow: 'hidden' }} onClick={() => setLightbox(selected.screenshot_url!)}>
                  <img
                    src={selected.screenshot_url}
                    alt="Screenshot"
                    style={{ width: '100%', display: 'block', borderRadius: 12, border: '4px solid #ffffff', boxShadow: '0 4px 12px rgba(25,28,30,0.1)' }}
                  />
                </div>
              </div>
            )}

            {/* Tag + Status selects */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#777587', marginBottom: 8, paddingLeft: 2 }}>Tag</label>
                <select
                  value={selected.tag ?? ''}
                  onChange={e => handleTag(selected.id, e.target.value)}
                  style={{
                    width: '100%', background: '#eceef0', border: 'none', borderRadius: 8,
                    padding: '9px 12px', fontSize: 13, fontWeight: 600, color: '#191c1e',
                    fontFamily: 'Inter, sans-serif', outline: 'none', cursor: 'pointer',
                  }}
                >
                  <option value="">No tag</option>
                  {TAGS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#777587', marginBottom: 8, paddingLeft: 2 }}>Status</label>
                <select
                  value={selected.status}
                  onChange={e => setStatus(selected.id, e.target.value as 'open' | 'resolved')}
                  style={{
                    width: '100%', background: '#eceef0', border: 'none', borderRadius: 8,
                    padding: '9px 12px', fontSize: 13, fontWeight: 600, color: '#191c1e',
                    fontFamily: 'Inter, sans-serif', outline: 'none', cursor: 'pointer',
                  }}
                >
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
            </div>

            {/* Device Info */}
            <div style={{ marginBottom: 24, borderTop: '1px solid rgba(199,196,216,0.3)', paddingTop: 20 }}>
              <details>
                <summary style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  listStyle: 'none', cursor: 'pointer', userSelect: 'none',
                }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#777587' }}>Device Information</span>
                  <span style={{ fontSize: 14, color: '#777587' }}>▾</span>
                </summary>
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 2 }}>
                  {[
                    ['Model', selected.device_model],
                    ['OS', selected.os_version],
                    ['App Version', selected.app_version],
                    ['Platform', selected.platform],
                    ['Email', selected.email],
                    ['Submitted', new Date(selected.created_at * 1000).toLocaleString()],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: '#777587' }}>{k}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#191c1e' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </details>
            </div>

            {/* Internal Notes */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#777587', marginBottom: 10, paddingLeft: 2 }}>
                Internal Notes
              </label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Type a private note for the team..."
                style={{
                  width: '100%', background: '#eceef0', border: 'none', borderRadius: 12,
                  padding: 14, fontSize: 13, minHeight: 100, color: '#191c1e',
                  fontFamily: 'Inter, sans-serif', resize: 'vertical', outline: 'none',
                  boxSizing: 'border-box', lineHeight: 1.5,
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  onClick={saveNote}
                  disabled={savingNote}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700, color: '#3525cd',
                    textDecoration: savingNote ? 'none' : 'underline',
                    opacity: savingNote ? 0.5 : 1,
                  }}
                >
                  {savingNote ? 'Saving…' : 'Save Note'}
                </button>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={() => setStatus(selected.id, selected.status === 'open' ? 'resolved' : 'open')}
              style={{
                width: '100%', padding: '14px', background: '#3525cd', color: '#ffffff',
                border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                boxShadow: '0 8px 24px rgba(53,37,205,0.25)',
                transition: 'opacity 0.15s',
              }}
            >
              {selected.status === 'open' ? 'Mark as Resolved' : 'Reopen Feedback'}
            </button>
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(25,28,30,0.7)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out',
          }}
        >
          <img
            src={lightbox}
            alt="Screenshot"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 24px 60px rgba(25,28,30,0.3)' }}
          />
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: 'absolute', top: 20, right: 24,
              background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
              border: 'none', borderRadius: '50%', width: 36, height: 36,
              color: '#fff', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      <style>{`
        .feedback-row:hover { background: #eceef0 !important; }
        .feedback-row:hover .delete-btn { opacity: 1 !important; }
        .feedback-row:hover .delete-btn:hover { background: rgba(186,26,26,0.08) !important; color: #ba1a1a !important; }
      `}</style>

      {confirmDelete && (
        <ConfirmModal
          title="Delete Feedback"
          message="This feedback will be permanently deleted and cannot be recovered."
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteFeedback}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}
